/**
 * Argalis V2 - useTenantConfig Hook
 *
 * Récupère la configuration effective du tenant depuis l'API.
 * Fusionne: Plan de base + Stripe Addons + Manual Grants
 */

import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { API_BASE, apiFetch } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'

export interface FeatureInfo {
    id: string
    name: string
    description: string
    is_beta: boolean
}

export interface TenantConfig {
    plan: 'focus' | 'boost' | 'business'
    effective_features: FeatureInfo[]
    max_users: number | null
    max_entities: number | null
    /**
     * 36_CUSTOM / CUSTOM-001 §5.5 — le plafond EFFECTIF de champs personnalisés par fiche
     * (0 / 20 / 200, 20 sur octroi manuel ou option Stripe). C'est le N de « n sur N ».
     *
     * ⛔ **Il vient de l'API, jamais d'une constante d'écran.** Le calcul vit à UN endroit
     * côté serveur (`infra/custom_fields/_gate_de_plan.plafond_effectif`), partagé avec la
     * porte d'écriture dont le refus `422 CUSTOM_FIELD_DEFINITION_LIMIT` porte le même
     * nombre en `limit`. Recopié ici, il divergerait au premier octroi : l'écran
     * annoncerait « 0 sur 0 » à qui peut créer vingt champs.
     *
     * ⚠️ Facultatif : un serveur antérieur au 2026-09-17 ne l'envoie pas. Absent, il vaut
     * INCONNU — jamais zéro, qui se lirait « vous ne pouvez rien créer ».
     */
    max_custom_fields_per_target?: number
    stripe_addons: {
        features: string[]
        extra_seats: number
    }
    manual_grants: Record<string, unknown>
    /**
     * Le JOUR de l'organisme (`YYYY-MM-DD`), calculé par le serveur dans le fuseau
     * de `tenant_config.timezone` (décisions PO D43/D60 du 2026-09-23). C'est le jour
     * qui décide des refus « date dans le futur » : un sélecteur qui pré-remplit ou
     * borne au jour du NAVIGATEUR s'en écarte d'un jour dès que les fuseaux divergent.
     * ⚠️ Facultatif : un serveur antérieur ne l'envoie pas (`null`/absent → repli).
     */
    today?: string | null
    /**
     * 30_LOG / LOC-009 (2026-09-23) — le fuseau de RÉFÉRENCE de l'organisme
     * (`tenant_config.timezone`, repli Paris côté serveur). La fiche d'un lieu
     * n'annonce le fuseau déduit de l'adresse que s'il n'a pas la même heure que
     * celui-ci. ⚠️ Facultatif : un serveur antérieur ne l'envoie pas.
     */
    timezone?: string | null
    /**
     * Le nombre d'entités ACTIVES de l'organisme — toutes, pas celles attribuées à la personne
     * connectée (`/auth/me` ne liste toutes les entités qu'au Super Admin de la racine et au
     * créateur de l'organisme). Lu par `useOrganismeMultiEntites`.
     * ⚠️ Facultatif : un serveur antérieur ne l'envoie pas (absent → INCONNU).
     */
    entity_count?: number
}

interface UseTenantConfigReturn {
    config: TenantConfig | null
    plan: 'focus' | 'boost' | 'business'
    features: string[]
    maxUsers: number | null
    maxEntities: number | null
    /**
     * Le N de « n sur N » des champs personnalisés — `null` tant que l'offre est INCONNUE
     * (chargement, repli, ou serveur qui ne l'envoie pas encore). ⛔ Jamais `0` par défaut :
     * un écran qui affiche « 3 sur 0 » accuse le produit d'un plafond qui n'existe pas.
     */
    maxCustomFieldsPerTarget: number | null
    /** Le jour de l'organisme servi par l'API (`YYYY-MM-DD`) — `null` tant qu'il est
     *  INCONNU (chargement, repli, serveur ancien). Lu par `useJourDeLOrganisme`. */
    today: string | null
    /** Le fuseau de l'organisme — `null` tant qu'il est INCONNU. */
    timezone: string | null
    isLoading: boolean
    error: Error | null
    hasFeature: (featureId: string) => boolean
    isBusiness: boolean
    isBoost: boolean
    isFocus: boolean
    refetch: () => Promise<void>
}

/**
 * Valeurs de REPLI, affichées le temps que l'API réponde — jamais une vérité.
 *
 * ⚠️ Ce jeu est incohérent avec lui-même, et c'est insoluble : un plan `business`
 * SANS aucune feature n'existe pas. Chaque lecteur en tire donc une réponse fausse
 * dans un sens ou dans l'autre — `isBusiness` répond oui à tort, `hasFeature`
 * répond non à tort. D'où la règle : on ne décide RIEN tant que `isLoading` est
 * vrai (voir la note sur `isLoading` plus bas).
 */
const DEFAULT_CONFIG: TenantConfig = {
    plan: 'business',
    effective_features: [],
    max_users: null,
    max_entities: null,
    stripe_addons: { features: [], extra_seats: 0 },
    manual_grants: {},
}

async function fetchTenantConfig(): Promise<TenantConfig> {
    const response = await apiFetch(`${API_BASE}/tenant/config`)
    if (!response.ok) throw new Error(`Failed to fetch tenant config: ${response.status}`)
    return response.json()
}

export function useTenantConfig(): UseTenantConfigReturn {
    const { data: config, isLoading: queryLoading, isPlaceholderData, error, refetch } = useQuery({
        queryKey: queryKeys.tenantConfig.all,
        queryFn: fetchTenantConfig,
        staleTime: 5 * 60_000,
        placeholderData: DEFAULT_CONFIG,
    })

    // ⚠️ CORRECTIF DU 2026-08-13 — `isLoading` MENTAIT, et une douzaine d'appelants
    // s'y fiaient.
    //
    // `placeholderData` fait passer la requête en `success` dès le premier rendu :
    // TanStack renvoie donc `isLoading: false` alors que la config n'est pas encore
    // arrivée (mesuré : `isLoading: false`, `isPlaceholderData: true`,
    // `effective_features: []`). Tout garde de la forme `!isLoading && !hasFeature(x)`
    // se déclenchait donc sur un placeholder VIDE.
    //
    // Ce que ça cassait, constaté par le PO : ouvrir /settings/workflows après un
    // rafraîchissement de page renvoyait aussitôt vers /automation/workflows — la page
    // se croyait sur un plan sans atelier. Le cache masquait le défaut en navigation
    // ordinaire (staleTime 5 min) ; seul un rechargement, cache vide, le révélait.
    // Symétriquement, le garde `isBusiness && !isLoading` de SALE-025, écrit pour
    // empêcher un bouton business de CLIGNOTER chez FOCUS/BOOST, ne protégeait de rien.
    //
    // Corrigé ICI et pas dans les appelants : le défaut est dans la source, et un
    // correctif rangé dans une page ne se retrouve jamais depuis les onze autres.
    const isLoading = queryLoading || isPlaceholderData

    const effectiveConfig = config || DEFAULT_CONFIG
    // ⚠️ `config || DEFAULT_CONFIG` garde le `null`, PAS le corps malformé : un
    // 200 sans `effective_features` passait le `||` et faisait lever `.map` —
    // une exception dans un hook appelé par onze pages, donc un écran blanc
    // remplacé par la phrase muette de l'`ErrorBoundary`. Le repli se pose sur
    // le CHAMP, là où l'absence se produit.
    const features = (effectiveConfig.effective_features ?? []).map(f => f.id)

    const hasFeature = useCallback(
        (featureId: string): boolean => features.includes(featureId),
        [features],
    )

    return {
        config: config ?? null,
        plan: effectiveConfig.plan,
        features,
        maxUsers: effectiveConfig.max_users,
        maxEntities: effectiveConfig.max_entities,
        // ⛔ Ce qui empêche l'écran d'annoncer « 4 sur 0 » une fraction de seconde — le nombre
        // le plus faux possible, puisqu'il dit le contraire de ce que le plan permet —, c'est
        // le `?? null` ET le fait que `DEFAULT_CONFIG` ne porte AUCUN plafond. Mesuré le
        // 2026-09-18 : `placeholderData` fait passer la requête en `success` dès le premier
        // rendu, donc `config` VAUT le repli le temps de l'aller-retour, et le repli n'a pas
        // la clé. ⚠️ `isLoading ?` est donc une CEINTURE, pas la barrière : retiré, aucun
        // témoin ne rougit (sabotage joué). Il est gardé parce qu'il coûte zéro et qu'il tient
        // le jour où le repli porterait un plafond — jour que `useTenantConfig.test.tsx`
        // surveille par un témoin dédié (« le repli ne porte aucun plafond »).
        maxCustomFieldsPerTarget: isLoading ? null : (config?.max_custom_fields_per_target ?? null),
        // Même règle que le plafond : le repli (`DEFAULT_CONFIG`) ne porte aucun jour,
        // et un jour « deviné » pré-remplirait un formulaire d'une date fausse.
        today: isLoading ? null : (config?.today ?? null),
        // Même règle : un fuseau « deviné » ferait annoncer un décalage qui n'existe pas.
        timezone: isLoading ? null : (config?.timezone ?? null),
        isLoading,
        error: error ?? null,
        hasFeature,
        isBusiness: effectiveConfig.plan === 'business',
        isBoost: effectiveConfig.plan === 'boost',
        isFocus: effectiveConfig.plan === 'focus',
        refetch: async () => { await refetch() },
    }
}
